import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaLeaf, FaTools, FaStar, FaCertificate, FaAward } from 'react-icons/fa';
import { RiShieldCheckFill } from 'react-icons/ri';
import { BiDollarCircle } from 'react-icons/bi';

const Advantages = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const advantages = [
    {
      title: 'Expert Installation',
      description: 'Our certified installation team ensures perfect fit and maximum protection for your home.',
      icon: <FaTools className="w-8 h-8 text-primary-600" />,
      details: [
        'Factory-trained installers',
        'Precise measurements and fitting',
        'Clean and efficient installation process',
        'Minimal disruption to your home'
      ]
    },
    {
      title: 'Superior Products',
      description: 'We only use top-quality materials and trusted brands for lasting durability.',
      icon: <FaStar className="w-8 h-8 text-primary-600" />,
      details: [
        'Miami-Dade County approved products',
        'Energy Star certified windows and doors',
        'Industry-leading warranties',
        'Wide selection of styles and finishes'
      ]
    },
    {
      title: 'Customer Service',
      description: 'Experience exceptional service from consultation to installation and beyond.',
      icon: <FaCertificate className="w-8 h-8 text-primary-600" />,
      details: [
        '24/7 customer support',
        'Detailed product education',
        'Transparent pricing',
        'Post-installation follow-up'
      ]
    },
    {
      title: 'Proven Experience',
      description: '20+ years of experience serving South Florida homeowners.',
      icon: <FaAward className="w-8 h-8 text-primary-600" />,
      details: [
        'Thousands of successful installations',
        'Outstanding customer reviews',
        'Local business understanding',
        'Hurricane protection expertise'
      ]
    }
  ];

  const impactRatings = [
    {
      rating: 'DP-50',
      description: 'Withstands winds up to 170 mph',
      details: 'Ideal for most residential applications'
    },
    {
      rating: 'HVHZ',
      description: 'High Velocity Hurricane Zone Approved',
      details: 'Meets strictest Miami-Dade requirements'
    },
    {
      rating: 'Level E',
      description: 'Large Missile Impact Resistant',
      details: 'Maximum protection against flying debris'
    }
  ];

  const energyFeatures = [
    {
      title: 'Low-E Glass',
      description: 'Reflects heat while letting in natural light',
      savings: 'Up to 25% on energy bills'
    },
    {
      title: 'Argon Gas Fill',
      description: 'Improves insulation and reduces heat transfer',
      savings: 'Enhanced thermal performance'
    },
    {
      title: 'Warm Edge Spacers',
      description: 'Reduces thermal bridging and condensation',
      savings: 'Better comfort and efficiency'
    }
  ];

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">WHY CHOOSE US</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Why Choose Ernesto Windows & Doors?
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            With over two decades of experience, we're your trusted partner in home improvement.
            Discover what makes us the preferred choice for impact windows and doors in South Florida.
          </p>
        </motion.div>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {advantages.map((advantage, index) => (
            <motion.div
              key={advantage.title}
              initial={fadeIn.initial}
              whileInView={fadeIn.animate}
              viewport={{ once: true }}
              transition={{ ...fadeIn.transition, delay: index * 0.2 }}
              className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition duration-300"
            >
              <div className="mb-4">{advantage.icon}</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {advantage.title}
              </h3>
              <p className="text-gray-600 mb-6">
                {advantage.description}
              </p>
              <ul className="space-y-3">
                {advantage.details.map((detail, idx) => (
                  <li key={idx} className="flex items-center text-gray-600">
                    <RiShieldCheckFill className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Impact Ratings Section */}
        <motion.section
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">IMPACT RATINGS</span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Superior Impact Protection</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our products meet and exceed the most stringent hurricane protection standards
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactRatings.map((rating, index) => (
              <motion.div
                key={rating.rating}
                initial={fadeIn.initial}
                whileInView={fadeIn.animate}
                viewport={{ once: true }}
                transition={{ ...fadeIn.transition, delay: index * 0.2 }}
                className="bg-white rounded-lg shadow-md p-8 text-center hover:shadow-lg transition duration-300"
              >
                <FaShieldAlt className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{rating.rating}</h3>
                <p className="text-lg font-medium text-primary-600 mb-4">{rating.description}</p>
                <p className="text-gray-600">{rating.details}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Energy Efficiency Section */}
        <motion.section
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="mb-16 bg-gray-50 rounded-lg p-12"
        >
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">ENERGY SAVINGS</span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Maximum Energy Efficiency</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our energy-efficient products help reduce your utility bills while protecting the environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {energyFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={fadeIn.initial}
                whileInView={fadeIn.animate}
                viewport={{ once: true }}
                transition={{ ...fadeIn.transition, delay: index * 0.2 }}
                className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition duration-300"
              >
                <FaLeaf className="w-10 h-10 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="flex items-center text-primary-600 font-medium">
                  <BiDollarCircle className="w-5 h-5 mr-2" />
                  {feature.savings}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Certifications Section */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="bg-white rounded-lg p-12 shadow-md mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Certifications & Partnerships
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition duration-300">
              <RiShieldCheckFill className="w-12 h-12 text-primary-600 mb-3" />
              <span className="text-gray-900 font-semibold">Miami-Dade County</span>
              <span className="text-sm text-gray-600">NOA Approved</span>
            </div>
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition duration-300">
              <FaLeaf className="w-12 h-12 text-primary-600 mb-3" />
              <span className="text-gray-900 font-semibold">Energy Star</span>
              <span className="text-sm text-gray-600">Certified Partner</span>
            </div>
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition duration-300">
              <FaCertificate className="w-12 h-12 text-primary-600 mb-3" />
              <span className="text-gray-900 font-semibold">AAMA Certified</span>
              <span className="text-sm text-gray-600">Installation</span>
            </div>
            <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition duration-300">
              <FaStar className="w-12 h-12 text-primary-600 mb-3" />
              <span className="text-gray-900 font-semibold">BBB A+ Rating</span>
              <span className="text-sm text-gray-600">Accredited Business</span>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="text-center bg-primary-600 rounded-lg p-12 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl mb-8">
            Join thousands of satisfied homeowners who trust Ernesto Windows & Doors.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/get-estimate"
              className="bg-white text-primary-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition duration-300"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/products"
              className="border-2 border-white text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-white hover:text-primary-600 transition duration-300"
            >
              Browse Our Products
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Advantages; 